import React, { useState, useRef, useEffect } from 'react';
import { Box, TextField, Typography, styled } from '@mui/material';

const CodeContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  gap: theme.spacing(1),
  justifyContent: 'center',
  alignItems: 'center',
}));

const CodeInput = styled(TextField)(({ theme }) => ({
  '& .MuiInputBase-input': {
    textAlign: 'center',
    fontSize: '1.5rem',
    fontWeight: 'bold',
    fontFamily: 'monospace',
    padding: theme.spacing(1.5),
    width: '3rem',
    height: '3rem',
  },
  '& .MuiOutlinedInput-root': {
    '&.Mui-focused fieldset': {
      borderColor: theme.palette.primary.main,
      borderWidth: 2,
    },
  },
}));

interface VerificationCodeInputProps {
  length?: number;
  onComplete: (code: string) => void;
  disabled?: boolean;
  error?: boolean;
  helperText?: string;
}

const VerificationCodeInput: React.FC<VerificationCodeInputProps> = ({
  length = 6,
  onComplete,
  disabled = false,
  error = false,
  helperText,
}) => {
  const [values, setValues] = useState<string[]>(new Array(length).fill(''));
  const [hasCompleted, setHasCompleted] = useState(false);
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  useEffect(() => {
    // Focus first input on mount
    if (inputRefs.current[0] && !disabled) {
      inputRefs.current[0].focus();
    }
  }, [disabled]);

  useEffect(() => {
    // Check if all fields are filled
    const code = values.join('');
    if (code.length === length && code.replace(/\s/g, '') === code && !hasCompleted) {
      setHasCompleted(true);
      onComplete(code);
    } else if (code.length !== length) {
      setHasCompleted(false);
    }
  }, [values, length, onComplete, hasCompleted]);

  const handleChange = (index: number, value: string) => {
    // Only allow digits
    const sanitizedValue = value.replace(/[^0-9]/g, '');
    
    if (sanitizedValue.length <= 1) {
      const newValues = [...values];
      newValues[index] = sanitizedValue;
      setValues(newValues);

      // Auto-focus next input
      if (sanitizedValue && index < length - 1) {
        inputRefs.current[index + 1]?.focus();
      }
    }
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent) => {
    if (e.key === 'Backspace' && !values[index] && index > 0) {
      // Focus previous input on backspace if current is empty
      inputRefs.current[index - 1]?.focus();
    } else if (e.key === 'ArrowLeft' && index > 0) {
      inputRefs.current[index - 1]?.focus();
    } else if (e.key === 'ArrowRight' && index < length - 1) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').replace(/[^0-9]/g, '');
    
    if (pastedData.length === length) {
      const newValues = pastedData.split('');
      setValues(newValues);
      // Focus last input
      inputRefs.current[length - 1]?.focus();
    }
  };

  const clearCode = () => {
    setValues(new Array(length).fill(''));
    setHasCompleted(false);
    if (inputRefs.current[0]) {
      inputRefs.current[0].focus();
    }
  };

  return (
    <Box>
      <CodeContainer>
        {values.map((value, index) => (
          <CodeInput
            key={index}
            inputRef={(el) => (inputRefs.current[index] = el)}
            value={value}
            onChange={(e) => handleChange(index, e.target.value)}
            onKeyDown={(e) => handleKeyDown(index, e)}
            onPaste={handlePaste}
            disabled={disabled}
            error={error}
            variant="outlined"
            inputProps={{
              maxLength: 1,
              autoComplete: 'off',
              inputMode: 'numeric',
              pattern: '[0-9]*',
            }}
          />
        ))}
      </CodeContainer>
      {helperText && (
        <Typography
          variant="body2"
          color={error ? 'error' : 'text.secondary'}
          sx={{ textAlign: 'center', mt: 1 }}
        >
          {helperText}
        </Typography>
      )}
    </Box>
  );
};

export default VerificationCodeInput;